import { Modal } from '@design-system/components/modal/modal';
import { LibraryVM } from '../view-models/library.viewModel';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFolder } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { Folder, Folders, Videos } from '../models/library.domain';
import { getDurationDisplay } from '../helpers';
import { Tag } from '@design-system/components/tag/tag';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { getFoldersChildrenCount } from '@client/new/common/library/helpers';

const getCurrentFolders = (
  allFolders: Folders,
  movedFolderIds: number[],
  selectedFolder?: Folder,
) => {
  if (!selectedFolder)
    return allFolders
      .filter((f) => f.parentId === null)
      .filter((f) => !movedFolderIds.includes(f.id));
  return allFolders
    .filter((f) => f.parentId === selectedFolder.id)
    .filter((f) => !movedFolderIds.includes(f.id));
};

const getCurrentVideos = (allVideos: Videos, selectedFolder?: Folder) => {
  if (!selectedFolder) return allVideos.filter((f) => f.folderId === null);
  return allVideos.filter((f) => f.folderId === selectedFolder.id);
};

export const MoveToFolderModal = (p: LibraryVM) => {
  const [selectedFolder, setSelectedFolder] = useState<Folder>();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef?.current) listRef.current.scrollTop = 0;
  }, [selectedFolder]);

  if (p.openModal.type !== 'MoveToFolder') {
    return null;
  }
  const { movedItems } = p.openModal.value;
  const currentLocalization = p.openModal.value.currentFolderName || 'Library';

  const setParentFolder = () => {
    const parentFolder = p.allFolders.find(
      (f) => f.id === selectedFolder?.parentId,
    );
    setSelectedFolder(parentFolder);
  };

  const folders = getCurrentFolders(
    p.allFolders,
    movedItems.folders,
    selectedFolder,
  );
  const videos = getCurrentVideos(p.allVideos, selectedFolder);

  return (
    <Modal
      isOpen={p.openModal.type === 'MoveToFolder'}
      onClose={() => p.dispatch('closeConfirmation')}
      title={`Move items (${movedItems.count})`}
      disableBackdropClick={true}
      modalClassName="w-full md:w-[600px] h-[470px]"
    >
      <div className="flex flex-col gap-6">
        <Typography>Current localization: {currentLocalization}</Typography>
        <div className="flex flex-col gap-4 h-full md:h-[250px]">
          {!selectedFolder ? null : (
            <div className="flex gap-4 items-center py-2 border-b-2">
              <FontAwesomeIcon icon={faArrowLeft} onClick={setParentFolder} />
              <div className="flex flex-col gap-1">
                <Typography variant="body" size="medium">
                  {selectedFolder.name}
                </Typography>
                <Typography variant="body" size="small" color="N-80">
                  {getFoldersChildrenCount(
                    p.allFolders,
                    p.allVideos,
                    selectedFolder.id,
                  )}
                </Typography>
              </div>
            </div>
          )}
          <div
            className="flex flex-col gap-4 w-full md:w-[525px] h-full overflow-auto"
            ref={listRef}
          >
            {folders.map((folder) => (
              <div
                className={clsx(
                  'flex gap-4 px-[30px] cursor-pointer',
                  movedItems.folders.includes(folder.id) &&
                    'pointer-events-none',
                  movedItems.folders.includes(folder.id) &&
                    sprinkles({ color: 'N-60' }),
                )}
                key={folder.id}
                onClick={() => setSelectedFolder(folder)}
              >
                <FontAwesomeIcon icon={faFolder} />
                <div className="flex flex-col gap-1">
                  <Typography variant="body" size="medium">
                    {folder.name}
                  </Typography>
                  <Typography variant="body" size="small" color="N-80">
                    {getFoldersChildrenCount(
                      p.allFolders,
                      p.allVideos,
                      folder.id,
                    )}
                  </Typography>
                </div>
              </div>
            ))}
            {videos.map((video) => (
              <div key={video.id} className="relative">
                <div
                  className={clsx(
                    'absolute w-full h-full',
                    sprinkles({ bg: 'W-100/50' }),
                  )}
                  style={{ zIndex: 2 }}
                />
                <div className="flex gap-4 px-[30px]">
                  <div className="flex mr-auto gap-2">
                    <div className="flex w-[52px] items-center justify-center">
                      <img
                        src={video.thumbnailUrl}
                        className={clsx(
                          'max-w-[52px] max-h-[52px]',
                          sprinkles({ clipPath: 'sm' }),
                        )}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography
                        variant="body"
                        size="medium"
                        className="text-ellipsis whitespace-nowrap overflow-hidden max-w-[200px]"
                      >
                        {video.name}
                      </Typography>
                      <Typography variant="body" size="small" color="N-80">
                        {getDurationDisplay(video.duration)}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex w-[80px] justify-center">
                    <Tag label={video.type === 'short' ? 'Clip' : 'Video'} />
                  </div>
                  <Typography>
                    {new Date(video.updatedAt).toLocaleDateString('en-US')}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-center gap-4">
          <Button
            variant="text"
            color="neutral"
            className="w-full md:w-[254px]"
            onClick={() => p.dispatch('closeConfirmation')}
            disabled={p.updateProgress}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            color="neutral"
            className="w-full md:w-[254px]"
            onClick={() =>
              p.dispatch('moveToFolder', selectedFolder?.id || null)
            }
            disabled={p.updateProgress}
            loading={p.updateProgress}
          >
            Move
          </Button>
        </div>
      </div>
    </Modal>
  );
};
